import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { User, Buildings, Folder, Receipt, File, ArrowLeft } from '@phosphor-icons/react';
import { getGedRegisters, getGedRegisterDocuments } from '../services/api';
import { Loader2 } from 'lucide-react';
import { PageHero } from '../components/motion/PageHero';
import { Books } from '@phosphor-icons/react';

const ICONS = { user: User, buildings: Buildings, folder: Folder, receipt: Receipt, file: File };

export default function GedRegistersPage() {
  const [registers, setRegisters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [groups, setGroups] = useState([]);
  const [groupLoading, setGroupLoading] = useState(false);

  useEffect(() => {
    getGedRegisters()
      .then((r) => setRegisters(r.data.items || []))
      .finally(() => setLoading(false));
  }, []);

  const openRegister = (key) => {
    setSelected(key);
    setGroupLoading(true);
    getGedRegisterDocuments(key)
      .then((r) => setGroups(r.data.groups || []))
      .finally(() => setGroupLoading(false));
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin" /></div>;

  if (selected) {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="ged-register-detail">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>
            <ArrowLeft size={16} className="mr-1" /> Retour aux registres
          </Button>
          <h1 className="text-2xl font-semibold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>
            Registre — {registers.find(r => r.key === selected)?.label}
          </h1>
        </div>
        {groupLoading ? (
          <div className="flex items-center justify-center h-40"><Loader2 className="animate-spin" /></div>
        ) : groups.length === 0 ? (
          <Card><CardContent className="p-5 text-center text-[#5E6D66] italic">Aucun document dans ce registre.</CardContent></Card>
        ) : groups.map(g => (
          <Card key={g.entity} className="border-[#E8E6E1]" data-testid={`register-group-${g.entity}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-[#1A2421]">{g.entity}</h3>
                <Badge variant="outline" className="text-xs">{g.documents.length} doc(s)</Badge>
              </div>
              <ul className="space-y-1">
                {g.documents.map(d => (
                  <li key={d.id} className="flex items-center justify-between text-sm py-1 border-b border-[#F1EFE9] last:border-0">
                    <div className="flex items-center gap-2">
                      <File size={14} className="text-[#5E6D66]" />
                      <span className="text-[#1A2421]">{d.filename}</span>
                      {d.category && <Badge variant="outline" className="text-[10px]">{d.category}</Badge>}
                    </div>
                    <span className="text-xs text-[#5E6D66] tabular-nums">{d.created_at ? new Date(d.created_at).toLocaleDateString('fr-FR') : ''}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="ged-registers-page">
      <PageHero
        title="Registres GED"
        subtitle="Classement virtuel des documents par entité et par nature"
        icon={<Books size={24} weight="duotone" className="text-white" />}
        gradient="from-[#00AEEF] to-[#2D5A4C]"
        accents={["#80C51F", "#FF8E00"]}
      />
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {registers.map(r => {
          const I = ICONS[r.icon] || File;
          return (
            <button
              key={r.key}
              onClick={() => openRegister(r.key)}
              className="text-left"
              data-testid={`register-card-${r.key}`}
            >
              <Card className="border-[#E8E6E1] hover:border-[#2D5A4C] transition-colors cursor-pointer">
                <CardContent className="p-5">
                  <div className="w-12 h-12 rounded-lg bg-[#2D5A4C]/10 flex items-center justify-center mb-3">
                    <I size={24} className="text-[#2D5A4C]" />
                  </div>
                  <p className="font-semibold text-[#1A2421]">{r.label}</p>
                  <p className="text-2xl font-bold text-[#2D5A4C] tabular-nums mt-1" data-testid={`register-count-${r.key}`}>{r.count}</p>
                  <p className="text-xs text-[#5E6D66]">document(s)</p>
                </CardContent>
              </Card>
            </button>
          );
        })}
      </div>
    </div>
  );
}
